import { useState } from 'react'

import { httpBatchLink, loggerLink } from '@trpc/client'
import {
  createTRPCReact,
  inferReactQueryProcedureOptions,
} from '@trpc/react-query'
import superjson from 'superjson'

import type { AppRouter } from '@trevize/api'

import { getQueryClient } from '../react-query'
import { getRuntimeConfig } from '../runtime-config'

export type { RouterInputs, RouterOutputs } from '@trevize/api/types'

export { TRPCClientError } from '@trpc/client'

const getTrpcUrl = () => getRuntimeConfig().apiOrigin

export const api = createTRPCReact<AppRouter>()

export type ReactQueryOptions = inferReactQueryProcedureOptions<AppRouter>

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient()

  const [trpcClient] = useState(() =>
    api.createClient({
      links: [
        loggerLink({
          enabled: (op) =>
            import.meta.env.DEV ||
            (op.direction === 'down' && op.result instanceof Error),
        }),
        // Complete JSON responses, not JSONL streams. httpBatchStreamLink sets
        // Transfer-Encoding: chunked and leaves the body empty until the
        // procedure finishes; Cloudflare HTTP/3 then fails the request with
        // net::ERR_QUIC_PROTOCOL_ERROR (status 200) — which is how Desktop
        // connect dies in production after ~9s.
        httpBatchLink({
          transformer: superjson,
          url: getTrpcUrl() + '/api/trpc',
          async headers() {
            return {
              'x-trpc-source': 'react',
            }
          },
        }),
      ],
    }),
  )

  return (
    <api.Provider client={trpcClient} queryClient={queryClient}>
      {props.children}
    </api.Provider>
  )
}

export function useTRPC() {
  return api.useUtils()
}

export { isTRPCClientError } from './utils'
